const DISPLAY_ENVIRONMENTS = ['equation', 'equation*', 'align', 'align*', 'aligned', 'gather', 'gather*', 'gathered', 'multline', 'multline*', 'array', 'cases', 'matrix', 'pmatrix', 'bmatrix', 'Bmatrix', 'vmatrix', 'Vmatrix'] as const;

/**
 * Convert common TeX/Mathpix delimiter variants into remark-math syntax.
 * Complete formulas are preserved; malformed or orphan delimiters are rendered
 * as readable code instead of being sent to KaTeX as broken expressions.
 */
export function normalizeMathMarkdown(input: string): string {
  let value = String(input || '')
    .replace(/\r\n?/g, '\n')
    .replace(/\u00a0/g, ' ')
    .replace(/\\label\{[^}]*\}/g, '');

  const protectedValues: string[] = [];
  const protect = (content: string) => `\u0000FD_MATH_${protectedValues.push(content) - 1}\u0000`;

  // Markdown code must never be interpreted as mathematics.
  value = value.replace(/^ {0,3}(`{3,}|~{3,})[^\n]*\n[\s\S]*?^ {0,3}\1\s*$/gm, (match) => protect(match));
  value = value.replace(/`+[^`\n]+`+/g, (match) => protect(match));

  // Normalize any complete multi-dollar fence to the standard display form.
  value = value.replace(/(\${2,})([\s\S]*?)\1/g, (_match, _fence: string, formula: string) => protect(formatDisplayFormula(formula)));
  value = value.replace(/\\\[([\s\S]*?)\\\]/g, (_match, formula: string) => protect(formatDisplayFormula(formula)));

  for (const environment of DISPLAY_ENVIRONMENTS) {
    const escaped = escapeRegExp(environment);
    const expression = new RegExp(`\\\\begin\\{${escaped}\\}(?:\\{[^}]*\\})?[\\s\\S]*?\\\\end\\{${escaped}\\}`, 'g');
    value = value.replace(expression, (formula) => protect(formatDisplayFormula(formula)));
  }

  // Convert complete inline TeX delimiters.
  value = value.replace(/\\\(([^\n]*?)\\\)/g, (_match, formula: string) => protect(`$${formula.trim()}$`));
  value = protectInlineDollarMath(value, protect);

  // Remaining TeX delimiters/environments are incomplete. Show them as code so
  // the page stays readable and never produces a red KaTeX parse error.
  value = value
    .replace(/\\(?:begin|end)\{[A-Za-z*]+\}/g, (token) => `\`${token}\``)
    .replace(/\\(?:\[|\]|\(|\))/g, (token) => `\`${token}\``)
    .replace(/\$\$/g, () => protect('`$$`'))
    .replace(/(^|[^\\])\$/g, '$1\\$');

  value = value.replace(/\u0000FD_MATH_(\d+)\u0000/g, (_match, index: string) => protectedValues[Number(index)] || '');
  value = value
    .replace(/[ \t]+\n/g, (whitespace) => {
      const trailing = whitespace.slice(0, -1);
      return !trailing.includes('\t') && trailing.length >= 2 ? '  \n' : '\n';
    })
    .replace(/\n{4,}/g, '\n\n\n')
    .trim();

  // Mathpix sometimes emits a complete formula without delimiters. Only wrap it
  // when the entire value is clearly mathematical and structurally balanced.
  if (looksLikeStandaloneLatex(value) && hasBalancedBraces(value)) return formatDisplayFormula(value).trim();
  return value;
}

function protectInlineDollarMath(value: string, protect: (content: string) => string): string {
  let result = '';
  let index = 0;
  while (index < value.length) {
    if (value[index] !== '$' || value[index + 1] === '$' || isEscaped(value, index)) {
      result += value[index];
      index += 1;
      continue;
    }
    const close = findClosingDollar(value, index + 1);
    if (close < 0) {
      result += value[index];
      index += 1;
      continue;
    }
    const formula = value.slice(index + 1, close).trim();
    if (!formula) {
      result += '$$';
      index = close + 1;
      continue;
    }
    result += protect(`$${formula}$`);
    index = close + 1;
  }
  return result;
}

function findClosingDollar(value: string, start: number): number {
  for (let index = start; index < value.length; index += 1) {
    if (value[index] === '\n') return -1;
    if (value[index] === '$' && value[index + 1] !== '$' && !isEscaped(value, index)) return index;
  }
  return -1;
}

function formatDisplayFormula(input: string): string {
  let formula = input.trim()
    .replace(/\\label\{[^}]*\}/g, '');

  formula = formula
    .replace(/^\\begin\{equation\*?\}\s*/, '')
    .replace(/\s*\\end\{equation\*?\}$/, '')
    .trim();

  return `\n\n$$\n${formula}\n$$\n\n`;
}

function looksLikeStandaloneLatex(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 4000 || /[\u3400-\u9fff]/u.test(trimmed)) return false;
  if (/`|https?:\/\/|\n\n|\$/.test(trimmed)) return false;
  if (/\\(?:begin|end)\{/.test(trimmed)) return false;
  return /\\(?:frac|dfrac|tfrac|sqrt|sum|prod|int|left|right|partial|Delta|Gamma|ln|log|bar|overline|tilde|hat|cdot|times|equiv)|[_^]\s*\{/.test(trimmed);
}

function hasBalancedBraces(value: string): boolean {
  let depth = 0;
  for (let index = 0; index < value.length; index += 1) {
    if (isEscaped(value, index)) continue;
    if (value[index] === '{') depth += 1;
    if (value[index] === '}') depth -= 1;
    if (depth < 0) return false;
  }
  return depth === 0;
}

function isEscaped(value: string, index: number): boolean {
  let slashes = 0;
  for (let cursor = index - 1; cursor >= 0 && value[cursor] === '\\'; cursor -= 1) slashes += 1;
  return slashes % 2 === 1;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
